import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  FlagView, GameView, LogEntry, LogEvent, RobotView, TokenView,
} from '../api.js';

/**
 * Plays a register's movement back one step at a time.
 *
 * The server resolves a whole register in one advance and sends the final
 * positions, so a Move 3 arrives as a single jump from start to finish.
 * That is correct as state and useless as an explanation: a player cannot
 * see that they moved two squares, were pushed, and then rode a belt.
 *
 * The intermediate positions are already recorded, because every movement
 * event carries `from` and `to`. So this walks the log for the register
 * just resolved and hands back a set of positions that catches up to the
 * server one step at a time.
 *
 * The engine is not involved and no rule is re-evaluated here. This only
 * decides WHEN to show a position the server already computed. If
 * playback is interrupted — a reload, a slow fetch, a step that cannot be
 * matched — the display falls straight back to the server's positions,
 * which are always the truth.
 */

export interface DisplayRobot {
  x: number;
  y: number;
  facing: RobotView['facing'];
  /** Damage as of this point in the replay. The board used to read damage
   * off the server's state, which already includes the whole register —
   * so a laser hit showed on the token during MOVEMENT, several seconds
   * before the beam fired. */
  damage: number;
}

// Half a second between steps.
const STEP_MS = 500;

/** Movement events carry a destination; those are the steps worth
 * showing. Destruction is a step too, so a robot is seen to die rather
 * than simply being absent. */
export const DESTRUCTION = new Set([
  'destroyedPit', 'destroyedOffBoard', 'destroyedByDamage', 'crusherDestroyed',
  // A trap door opening under a robot that stayed put. Without it here
  // the death was not a step at all, so the robot was simply absent at
  // the end of the register instead of being seen to die ON the pit,
  // which is what an ordinary pit does.
  'trapDoorDestroyed',
]);

/**
 * Events that add damage, and how much.
 *
 * Where an event carries a count it is used — a double laser does two —
 * and otherwise a point is assumed, which is what every other source
 * deals. An event not listed here changes nothing on the token.
 */
const DAMAGE_EVENTS = new Set([
  'boardLaserHit', 'robotLaserHit', 'rearLaserHit', 'flamerDamage', 'flamerBurn',
  'flamingOilBurn', 'rampLandingDamage', 'bigGunHit', 'missileHit',
  'mineDamage', 'debrisDamage', 'seismicDamage',
  'flamingOilDamage', 'radioactiveWasteDamage', 'radiationDamage',
  'spikeDamage', 'spikeFloorDamage', 'speedBumpDamage', 'fallDamage',
  // Both fire and both hurt, and neither was here: a Mini-Howitzer round
  // and a laser a Reflector threw back down its own beam left the token
  // alone until the server's own damage arrived at the end of the
  // register.
  'miniHowitzerHit', 'laserReflected',
]);

/**
 * A shot in flight: the squares between shooter and target, and what to
 * draw along them.
 *
 * The board draws the run rather than the endpoints. A beam that covered
 * the shooter and the target would sit on top of both tokens, and it is
 * the empty space between them that says a shot happened.
 *
 * Null for anything not worth a line: an event that is not a shot, a
 * shooter the board cannot place -- every event written before the five
 * ray weapons recorded `sourceId` lands here -- adjacent robots with no
 * space between them, and the diagonal that no weapon in the game can
 * produce but which would otherwise be drawn as a row.
 */
export interface Shot {
  /** Top-left of the run, in cells. */
  x: number;
  y: number;
  /** How many squares it covers. Never zero. */
  cells: number;
  /** The way the shot travelled, shooter to target. The art is turned
   * to it: a shell has a nose and flies backwards without this, and a
   * north-south run has to be turned whatever is drawn on it. */
  towards: RobotView['facing'];
  /** The image, from `/tokens`. */
  art: string;
}

/** What each shot is drawn with. An event absent from here draws nothing:
 * a hit's consequences -- a shield, a reflector, a Fire Control lock --
 * are logged after the shot that caused them and would double the line. */
const SHOT_ART: Record<string, string> = {
  robotLaserHit: 'laser',
  rearLaserHit: 'laser',
  laserReflected: 'laser',
  // Guns, not lasers: a shell crossing the space rather than a beam.
  bigGunHit: 'projectile',
  miniHowitzerHit: 'projectile',
  beamPushed: 'beam',
  beamPulled: 'beam',
  scrambled: 'beam',
  radioControlled: 'beam',
};

export function shotFrom(
  event: LogEvent, positions: Map<string, DisplayRobot>,
): Shot | null {
  const art = SHOT_ART[event.type];
  if (!art || event.sourceId === undefined) return null;

  const from = positions.get(event.sourceId);
  const to = positions.get(event.robotId);
  if (!from || !to) return null;

  // The twin barrel is the one gun whose line differs, and the event says
  // so itself: reading it off the weapon's NAME would make the picture
  // depend on the wording of a card.
  const image = event.weapon === 'doubleLaser' && art === 'laser'
    ? 'doublelaser'
    : art;

  if (from.y === to.y) {
    const cells = Math.abs(to.x - from.x) - 1;
    if (cells < 1) return null;
    return {
      x: Math.min(from.x, to.x) + 1, y: from.y, cells,
      towards: to.x > from.x ? 'E' : 'W',
      art: `/tokens/${image}.png`,
    };
  }
  if (from.x === to.x) {
    const cells = Math.abs(to.y - from.y) - 1;
    if (cells < 1) return null;
    return {
      x: from.x, y: Math.min(from.y, to.y) + 1, cells,
      towards: to.y > from.y ? 'S' : 'N',
      art: `/tokens/${image}.png`,
    };
  }
  return null;
}

export function damageFrom(event: LogEvent): number {
  if (!DAMAGE_EVENTS.has(event.type)) return 0;
  return event.count ?? 1;
}

/**
 * A missile's own steps. These move a TOKEN, not a robot, and they carry
 * the owner's id so the log can file them -- so every caller that reads
 * `robotId` as "the thing that moved" has to check this set first, or a
 * missile drags its firer across the board with it.
 */
export const MISSILE_STEPS = new Set([
  'missileFlew', 'missileExploded', 'missileLost',
]);

/**
 * A flag riding a belt. These move the FLAG layer and nothing else, and
 * carry no robot id at all, so every caller that reads `robotId` as "the
 * thing that moved" has to check this set first.
 *
 * A step of its own so the flag moves when the belts run rather than the
 * instant the register resolves, which is when the view carrying its new
 * square arrives.
 */
export const FLAG_STEPS = new Set(['flagConveyed']);

/**
 * A robot taking an archive: step E, the end of a register.
 *
 * The marker must not appear on the flag or the repair site before then.
 * The view carries where every marker ended up, so drawing from it put the
 * marker on the square at the top of the register -- before the robot had
 * been shown arriving, and sometimes before the flag it belongs to had
 * been carried there.
 *
 * These carry no square of their own: an archive is taken where the robot
 * is standing, and at this point in the playback that is exactly where the
 * board has it.
 *
 * `flagTouched` is not one of them. Every non-final flag is a repair site
 * as well, so scoring one logs `archivedAtFlag` in the same breath, and
 * taking both would spend a beat on nothing.
 */
export const ARCHIVE_STEPS = new Set(['archivedAtFlag', 'repairSiteTouched']);

/** Events that change what the board shows: a move, a hit, or a missile
 * crossing a square. */
export function playableSteps(events: LogEvent[]): LogEvent[] {
  return events.filter((e) => (
    (e.to !== undefined
      && (e.type === 'moved' || e.type === 'pushed'
        || e.type === 'portaled' || e.type === 'rotated'
        || e.type === 'teleported' || e.type === 'repulsed'
        || e.type === 'launched'))
    || DESTRUCTION.has(e.type)
    || DAMAGE_EVENTS.has(e.type)
    || MISSILE_STEPS.has(e.type)
    || FLAG_STEPS.has(e.type)
    || ARCHIVE_STEPS.has(e.type)
    // A shot that neither moves its target nor damages it: a Pressor or
    // Tractor Beam, a Scrambler, a Radio Control. Nothing else in this
    // list catches one, so the beam had no beat to be drawn on and the
    // ray weapons crossed the board invisibly.
    || SHOT_ART[e.type] !== undefined
  ));
}

/**
 * One playable step applied to a running set of positions, in place.
 *
 * Shared by the single-register replay and the whole-game one: they walk
 * the same events from the same recorded frames, so a difference between
 * them would be a bug in one of them rather than a choice.
 *
 * Removing the robot is how destruction is drawn -- absent from the map
 * means not on the board.
 */
export function applyStep(running: Map<string, DisplayRobot>, step: LogEvent): void {
  const previous = running.get(step.robotId);
  const hit = damageFrom(step);

  if (step.to !== undefined) {
    running.set(step.robotId, {
      x: step.to.x,
      y: step.to.y,
      facing: step.facing ?? previous?.facing ?? 'N',
      damage: (previous?.damage ?? 0) + hit,
    });
  } else if (hit > 0 && previous) {
    // A hit with no movement: the token ticks up in place.
    running.set(step.robotId, { ...previous, damage: previous.damage + hit });
  } else if (DESTRUCTION.has(step.type)) {
    running.delete(step.robotId);
  }
  // Anything else is a step that changes no position and no damage -- a
  // beam that pushes, pulls, scrambles or takes over, drawn for the line
  // it puts on the board. It used to fall into the delete above and take
  // the robot it was fired at off the board with it.
}

/** The squares a straight flight passes through, `from` excluded and
 * `to` included. Empty when nothing moved. */
function squaresAfter(
  from: { x: number; y: number }, to: { x: number; y: number },
): { x: number; y: number }[] {
  const dx = Math.sign(to.x - from.x);
  const dy = Math.sign(to.y - from.y);
  // A missile only ever flies along its facing, so a diagonal means the
  // two ends do not describe a flight this can walk. Jump it rather than
  // loop forever.
  if (dx !== 0 && dy !== 0) return [to];
  const distance = Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y));
  const path: { x: number; y: number }[] = [];
  for (let i = 1; i <= distance; i++) {
    path.push({ x: from.x + dx * i, y: from.y + dy * i });
  }
  return path;
}

/**
 * Splits a missile's flight into one step per square.
 *
 * The engine resolves a whole phase of flight at once and records only
 * the ends of it, the same way it records a whole register of movement at
 * once. Robots are already animated a square at a time, so a missile that
 * crossed two squares in a single beat looked wrong beside them -- and
 * the square it passes THROUGH is worth seeing, since a missile goes off
 * on the first thing it meets rather than the square it was aimed at.
 *
 * The last square keeps the event's own type, so an explosion still goes
 * off exactly where it did and does not cost an extra beat.
 */
export function expandMissileFlights(steps: LogEvent[]): LogEvent[] {
  const out: LogEvent[] = [];
  for (const step of steps) {
    if (!MISSILE_STEPS.has(step.type) || !step.from || !step.to) {
      out.push(step);
      continue;
    }
    const path = squaresAfter(step.from, step.to);
    // Went off without moving -- a wall on the very first square.
    if (path.length === 0) {
      out.push(step);
      continue;
    }
    let at = step.from;
    path.forEach((square, i) => {
      out.push({
        ...step,
        type: i === path.length - 1 ? step.type : 'missileFlew',
        from: at,
        to: square,
      });
      at = square;
    });
  }
  return out;
}

/**
 * The token layer after one missile step.
 *
 * Matched on the token id, which the event carries for exactly this.
 * Rows logged before it did fall back to the square the shell flew from,
 * which is unambiguous in practice: nothing stacks two missiles on one
 * square.
 */
export function afterMissileStep(tokens: TokenView[], step: LogEvent): TokenView[] {
  const isShell = (t: TokenView) => (
    step.tokenId !== undefined
      ? t.id === step.tokenId
      : t.kind === 'missile' && t.x === step.from?.x && t.y === step.from?.y
  );
  // Exploded, or flown off the edge: the shell is off the board.
  if (step.type !== 'missileFlew') return tokens.filter((t) => !isShell(t));
  if (!step.to) return tokens;
  const { x, y } = step.to;
  return tokens.map((t) => (isShell(t) ? { ...t, x, y } : t));
}

/**
 * The flag layer after one flag step.
 *
 * Matched on the flag's number, which the event carries as `count`. A
 * null layer stays null: the caller has no record of where the flags were
 * and is drawing the course's own, which a step cannot move.
 */
/** A robot's archive, as much of it as the board needs to draw one. */
export interface Archive {
  archiveMarker?: { x: number; y: number };
  archiveFlag?: number;
}

/**
 * The archives after one archive step.
 *
 * `at` is where the board has the robot standing, which is where the
 * archive is taken. A flag hands out an archive that belongs to the flag
 * and travels with it -- `count` is which flag -- and a wrench site or a
 * generator hands out one that belongs to the square.
 */
export function afterArchiveStep(
  archives: Map<string, Archive>,
  step: LogEvent,
  at: { x: number; y: number } | undefined,
): Map<string, Archive> {
  if (!at) return archives;
  const next = new Map(archives);
  next.set(step.robotId, {
    archiveMarker: { x: at.x, y: at.y },
    ...(step.type === 'repairSiteTouched' ? {} : { archiveFlag: step.count }),
  });
  return next;
}

export function afterFlagStep(
  flags: FlagView[] | null, step: LogEvent,
): FlagView[] | null {
  if (!flags || !step.to) return flags;
  const { x, y } = step.to;
  return flags.map((flag) => (flag.number === step.count ? { ...flag, x, y } : flag));
}

/**
 * Where a robot's archive marker is drawn.
 *
 * The archive IS the flag: a marker set on one rides with it, so it
 * belongs wherever the flag layer being drawn has that flag. Falls back to
 * the marker's own square, which is the answer for an archive taken on a
 * wrench site, for a course whose flags do not move, and for a frame that
 * did not record which flag a marker was on.
 *
 * Read off the drawn layer rather than the state so the marker keeps step
 * with the flag through a playback instead of jumping to the register's
 * result ahead of it.
 */
export function archiveSquare(
  robot: { archiveMarker?: { x: number; y: number }; archiveFlag?: number },
  flags: FlagView[] | null | undefined,
): { x: number; y: number } | undefined {
  if (robot.archiveFlag === undefined) return robot.archiveMarker;
  const on = flags?.find((f) => f.number === robot.archiveFlag && !f.lost);
  return on ? { x: on.x, y: on.y } : robot.archiveMarker;
}

/**
 * Everything on the board that MOVES and is not a robot, in one snapshot.
 *
 * One object rather than a layer apiece, because the flicker is always the
 * same mistake: a view carries the register already resolved, so a layer
 * drawn straight from it shows the move before the playback walks it and
 * then walks it again. Holding the tokens and not the flags is exactly as
 * broken as holding neither, and it is the shape of the code that decides
 * whether the next thing to move is held too.
 *
 * So there is one snapshot, taken by `layersOf` and held, walked and
 * released as a unit. A new moving thing is a field here and a step in the
 * run -- the holding takes care of itself.
 */
interface Layers {
  tokens: TokenView[];
  /** Null only from a server that does not send them, where the course
   * layout is what the board draws. */
  flags: FlagView[] | null;
  /** Where each robot's archive stands, by robot id. A marker moves at
   * step E and nowhere else, so it is held like anything else that moves
   * -- and a marker on a flag rides the flag layer above from there. */
  archives: Map<string, Archive>;
}

const layersOf = (view: GameView | null): Layers => ({
  tokens: view?.tokens ?? [],
  flags: view?.flags ?? null,
  archives: new Map((view?.robots ?? []).map((r) => [
    r.id, { archiveMarker: r.archiveMarker, archiveFlag: r.archiveFlag },
  ])),
});

export function useSteppedPlayback(
  view: GameView | null,
  enabled: boolean,
  /**
   * The whole log, newest first. Supplied by the caller rather than
   * fetched here, so the panel and the playback read one copy.
   *
   * Everything since the last thing played is animated, not just the
   * newest entry: a register is several advances now — a reveal, one per
   * robot's card, and a tail — so it produces several log entries.
   * Animating only the newest showed the tail alone, which is why a Move 3
   * appeared to jump rather than step.
   */
  entries: LogEntry[],
  /**
   * Whether the log fetch for this game has come back at least once.
   *
   * Without it an empty `entries` is ambiguous, and the ambiguity was a
   * bug: on a page load the state arrives before the log, so the first
   * pass through here saw no entries and recorded -1 as "the newest thing
   * already played". When the log then landed, everything in the game was
   * newer than -1, so the whole game was animated as one register and
   * every damage event in it was added on top of the server's total — a
   * damage figure climbing past the maximum on every reload.
   */
  logLoaded: boolean,
): {
  positions: Map<string, DisplayRobot> | null;
  playing: boolean;
  /** The shot to draw at this beat, or null where nothing fired. */
  shot: Shot | null;
  /** Robots destroyed during the register just played, left on the board
   * where they died so the player can see WHERE it happened. Cleared when
   * the next register starts. */
  wreckage: Map<string, DisplayRobot>;
  /**
   * The token layer to draw, or null to draw the view's own.
   *
   * Tokens are held back through a register for the same reason robot
   * positions are: the view's token layer is the state at the END of the
   * register, so a mine that went off is already gone and the debris it
   * left is already there before the robot has been shown reaching it.
   */
  tokens: TokenView[] | null;
  /**
   * The flags to draw, or null to draw the view's own.
   *
   * Held back through a register exactly as the tokens are, and moved on
   * the beat the belts run: the view's flags are where step C left them,
   * which is a square ahead of everything else on the board for as long
   * as the register takes to play.
   */
  flags: FlagView[] | null;
  /**
   * Where each robot's archive stands at this beat, or null to read the
   * view's own. A marker is taken at step E, so through a register it
   * belongs where the last one left it.
   */
  archives: Map<string, Archive> | null;
} {
  const [positions, setPositions] = useState<Map<string, DisplayRobot> | null>(null);
  const [playing, setPlaying] = useState(false);
  /**
   * Bumped when a run ends, to come back for whatever arrived while it
   * was playing. The effect refuses to disturb a run in flight, so this
   * is what lets the next register start the moment the last one is
   * over rather than waiting for some other dependency to change.
   */
  const [ended, setEnded] = useState(0);
  const [wreckage, setWreckage] = useState<Map<string, DisplayRobot>>(new Map());
  // The board as the playback has it, or null to draw the view's own.
  const [held, setHeld] = useState<Layers | null>(null);

  // The shot being drawn this beat, or null. One beat holds one line: a
  // register can fire several, and each is set by its own step and
  // cleared by whatever comes next.
  const [shot, setShot] = useState<Shot | null>(null);

  // The version whose events have already been played, so a re-render
  // does not replay them.
  /**
   * The log entry already played.
   *
   * Keyed on the ENTRY, not the state version, because the two no longer
   * arrive together: the state changes first and the log follows a moment
   * later. Keying on version meant the effect could fire while `newest`
   * was still the previous register — playing the wrong one, and then
   * refusing to play the right one when it arrived.
   */
  const playedSeq = useRef<number>(-1);
  const seenVersion = useRef<number>(-1);
  const cancelled = useRef(false);

  /**
   * Whether a run's timers are still going.
   *
   * A run in flight is never interrupted. It used to be: the effect
   * depends on the state version AND on the log, and those arrive
   * separately, so every advance re-ran it at least twice -- and a
   * register with an Option prompt in it several times more. Each re-run
   * tore the running playback down and rewound it, so the robot walked
   * its move, snapped back to where it started and walked it again.
   *
   * Worse across registers: the batch that was rewound was replayed
   * inside the NEXT register's run, so a move made in register 1 was seen
   * again in the middle of register 3.
   */
  const inFlight = useRef(false);

  /** The pending step timers, so an unmount can stop them. */
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  /**
   * The register the cross on screen belongs to, as `turn/register`.
   *
   * A register is several advances -- one per robot's card, then a tail
   * (server/src/games.ts) -- and each one starts a playback run of its
   * own. Clearing the wreckage at the top of every run therefore wiped
   * the cross on the very next advance, so a robot that drove into a pit
   * on its own card was struck out for a fraction of a second and gone.
   * The cross belongs to the REGISTER the robot died in, so it is held
   * until the register changes and not before.
   */
  const wreckedIn = useRef<string | null>(null);

  // Where the board was last showing each robot. Playback starts from
  // HERE rather than from positions reconstructed out of the events,
  // because the board is drawn from server state and server state already
  // holds the final positions. Reconstructing meant robots jumped to the
  // end, snapped back to the start, and then stepped forward — a double
  // jump before every replay.
  const settled = useRef<Map<string, DisplayRobot>>(new Map());

  // The board as it last settled, updated only when a playback finishes.
  // Holding it in a ref rather than deriving it from `playing` matters:
  // the state version bumps BEFORE the playback starts, so anything keyed
  // on `playing` snapshots the new board and holds that, which is no hold
  // at all.
  const settledLayers = useRef<Layers>(layersOf(view));

  // Held in a ref rather than a dependency: a new `view` object arrives on
  // every state push, and depending on it meant an unrelated re-render
  // tore down a playback halfway through.
  const latestView = useRef<GameView | null>(view);
  latestView.current = view;

  const version = view?.version ?? -1;

  /** Drops a run on the floor. Only two things do this: leaving a
   * playback phase, and the component going away. */
  const stopRun = () => {
    cancelled.current = true;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    inFlight.current = false;
  };

  useEffect(() => () => {
    cancelled.current = true;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    // Cleared here too, so a remount is not locked out by a run that no
    // longer exists.
    inFlight.current = false;
  }, []);

  // A LAYOUT effect, not an ordinary one: an ordinary effect runs after
  // the browser has painted, so the new state — with the robot already at
  // the end of its move — was drawn for one frame before the replay put
  // it back. That single frame is the stutter.
  useLayoutEffect(() => {
    const current = latestView.current;
    if (!current) return;

    /**
     * Where the server has every robot that is ON the board.
     *
     * DESTROYED ROBOTS ARE LEFT OUT. A wreck stays in the view until End
     * of Turn returns it to play, and everything this hook hands back is
     * drawn as present -- `Game.tsx` marks any robot with a display
     * position `destroyed: false`, because that is what tells a robot
     * being animated from one the playback has finished with. So a dead
     * robot went into `holding`, and every later register in the turn
     * opened by putting it back on its square, alive and unmarked, for
     * the length of the playback; it vanished again the moment the run
     * ended and the board fell back to server state.
     *
     * The one register a dead robot IS wanted in is the one it dies in,
     * where it has to be on the board to be seen to die. That is fetched
     * from the PREVIOUS run's positions instead, from before it died --
     * which is what the reinstatement loop below is for, and what it was
     * always written to expect.
     */
    const positionsNow = () => new Map(
      current.robots.filter((r) => !r.destroyed).map(
        (r) => [r.id, { x: r.x, y: r.y, facing: r.facing, damage: r.damage }],
      ),
    );

    /** What the board has settled on, for the next register to hold. */
    const settleLayers = () => { settledLayers.current = layersOf(current); };

    // First sight of this game: nothing to play back from.
    if (seenVersion.current === -1) {
      // But not until the log has actually been fetched. Keeping the
      // board's positions current is right either way; consuming this
      // branch is not, because it is the branch that decides what counts
      // as already played and it only runs once.
      if (!logLoaded) {
        settled.current = positionsNow();
        settleLayers();
        return;
      }
      seenVersion.current = version;
      // A game whose log really is empty leaves this at -1, which is
      // correct: there is nothing already played, so the first register
      // to produce entries animates.
      playedSeq.current = entries[0]?.seq ?? -1;
      settled.current = positionsNow();
      // The board as this session first saw it, so the first register it
      // plays has layers to hold while its log arrives.
      settleLayers();
      return;
    }

    // Outside a playback phase — dealing, programming, end of turn —
    // there is nothing to replay, but the record of where the board is
    // MUST still be kept current. Returning without updating it left a
    // position from an earlier register in `settled`, so the next replay
    // opened by putting the robot back where it had been a turn ago,
    // then jumping forward to the real starting square.
    if (!enabled) {
      // The one thing that cuts a run short. The board goes back to
      // server state, and `playedSeq` is carried FORWARD below rather
      // than rewound, so leaving mid-register does not leave that
      // register queued to replay when the next one starts.
      stopRun();
      // Not in a playback phase at all: programming, or end of turn. Any
      // wreckage belongs to a register that has finished.
      setWreckage(new Map());
      wreckedIn.current = null;
      // Release any hold taken while waiting for a log that never came:
      // outside a playback phase the board is drawn from server state.
      setPositions(null);
      settleLayers();
      setHeld(null);
      seenVersion.current = version;
      // Anything logged while not in a playback phase is passed over
      // rather than queued, so leaving the phase does not leave a replay
      // waiting to fire.
      //
      // Forwards only. The state version bumps before the log fetch for
      // it returns, so `entries` here can still be the previous state's,
      // and assigning its newest seq outright moved the marker BACKWARDS
      // — which is how a whole turn's registers ended up queued and
      // replayed from the beginning at the start of the next one.
      playedSeq.current = Math.max(playedSeq.current, entries[0]?.seq ?? -1);
      settled.current = positionsNow();
      return;
    }

    // A run in flight is left alone. The effect re-runs at least twice
    // per advance -- the state version and the log arrive separately --
    // and more than that in a register with an Option prompt in it, and
    // every one of those used to tear the running playback down and
    // rewind it. That is the flicker: the robot walks its move, snaps
    // back to where it started and walks it again. `finish` brings us
    // back here, so whatever arrives while a run plays is picked up the
    // moment it ends.
    if (inFlight.current) return;

    // Everything logged since the last thing played, oldest first — the
    // log arrives newest first, and these must run in the order they
    // happened.
    const fresh = entries
      .filter((e) => e.seq > playedSeq.current)
      .sort((a, b) => a.seq - b.seq);

    // Nothing to play until the log for this state has arrived — and the
    // log fetch lands a moment AFTER the version bump, so there is a gap.
    //
    // Returning here through that gap left the board drawing straight
    // from server state, which already holds the END of the move. That is
    // the flicker: a Move 2 appears with the robot two squares on, then
    // snaps back and steps out one square at a time once the log turns
    // up. Hold the board where it already is until there is something to
    // play, and the move is only ever seen once, forwards.
    if (fresh.length === 0) {
      if (seenVersion.current !== version && settled.current.size > 0) {
        const waiting = new Map(settled.current);
        // A robot the board has not shown before — one just returned to
        // play — has no held position, so it is drawn where it really is.
        for (const [id, at] of positionsNow()) {
          if (!waiting.has(id)) waiting.set(id, at);
        }
        setPositions(waiting);
        // And the layers that went with those positions, which the view
        // has already carried to the end of a register the log has not
        // arrived to describe yet.
        setHeld(settledLayers.current);
      }
      return;
    }
    seenVersion.current = version;
    playedSeq.current = fresh[fresh.length - 1].seq;
    cancelled.current = false;

    // Which register these entries belong to. Every entry logged during a
    // register carries its number (server/src/games.ts, `registerOf`), and
    // the newest of them is the one being played into. The turn is in the
    // key as well, so register 1 of one turn is not mistaken for register
    // 1 of the next.
    const newest = fresh[fresh.length - 1];
    const registerNow = `${newest.turn}/${newest.register}`;

    /**
     * Drops the cross, but only once the register that put it there is
     * over. Called at the top of every run; does nothing on the other
     * advances of the register the wreck belongs to.
     */
    const endWreckageOfPreviousRegister = () => {
      if (wreckedIn.current === registerNow) return;
      wreckedIn.current = registerNow;
      setWreckage(new Map());
    };

    const finalPositions = positionsNow();

    // What goes with `finalPositions`, taken from the same view rather
    // than from whatever has arrived by the time the run ends -- which by
    // then is a state further on.
    const layersAfter = layersOf(current);

    const finish = () => {
      inFlight.current = false;
      timers.current = [];
      settled.current = finalPositions;
      // The register has been played out, so what it produced can be
      // shown: the debris now has a fall to have come from, and the flags
      // have been carried to where the view has had them all along. This
      // is also the board the NEXT register holds while its log arrives.
      settledLayers.current = layersAfter;
      setPositions(null);
      setHeld(null);
      setShot(null);
      setPlaying(false);
      // Come back for anything that landed while this was playing.
      setEnded((n) => n + 1);
    };

    /**
     * Ends the run a beat AFTER its last step, not in the same tick.
     *
     * `finish` clears the shot, so calling it alongside the step that
     * drew one set and cleared the line in a single render and the shot
     * was never on screen. A Scrambler is logged alone in its own
     * advance, so its beam was ALWAYS the last step of a run and never
     * appeared at all; a laser was seen only when something else in the
     * register happened to follow it.
     *
     * Two beats, for the reason the single-register replay holds them:
     * the last step is the one worth looking at, and one beat is barely
     * time to register it before the live board cuts back in.
     */
    const endRun = () => {
      timers.current.push(setTimeout(finish, STEP_MS * 2));
    };

    // Hold the board where it already is before anything else happens.
    const holding = new Map(settled.current);
    for (const [id, at] of finalPositions) {
      if (!holding.has(id)) holding.set(id, at);
    }
    for (const id of [...holding.keys()]) {
      if (!finalPositions.has(id)) holding.delete(id);
    }

    const events = fresh.flatMap((entry) => entry.events);
    const steps = expandMissileFlights(playableSteps(events));

    /**
     * The flags as this run STARTS, wound back out of the view's.
     *
     * Rewound rather than remembered from the last run: the view holds
     * where each flag is now, and every step that moved one carries the
     * square it came from, so the two together are the layer the register
     * began with -- and a reload part-way through a game has it too,
     * where a remembered layer would have nothing.
     *
     * A flag the board TOOK this register is not wound back. It is at its
     * starting square in the view, having already been returned there,
     * and there is no honest square to put it on while the register
     * plays: the log is what says it went.
     */
    const layersBefore: Layers = {
      // What the board was already showing, NOT the view's, which is the
      // state after the register.
      tokens: settledLayers.current.tokens,
      // The archives as they stood going in. Every one this register
      // hands out is a step, so they are walked from here.
      archives: settledLayers.current.archives,
      flags: current.flags?.map((flag) => {
        const moved = steps.find((s) => FLAG_STEPS.has(s.type) && s.count === flag.number);
        return moved?.from ? { ...flag, x: moved.from.x, y: moved.from.y } : flag;
      }) ?? null,
    };

    if (steps.length === 0) {
      // Nothing to animate — but a cross left from an earlier register
      // must still go. Clearing it only when a playable register arrived
      // left a destroyed robot marked dead through End of Turn and into
      // the next turn's programming, over the top of the robot that had
      // already come back.
      endWreckageOfPreviousRegister();
      finish();
      return;
    }

    inFlight.current = true;
    timers.current = [];

    setPositions(new Map(holding));
    setHeld(layersBefore);
    setShot(null);
    setPlaying(true);
    endWreckageOfPreviousRegister();

    // Continue from the hold. A robot destroyed during this register is
    // still on the board at the start of it, so it is put back and shown
    // until its destruction step.
    const running = new Map(holding);
    for (const event of steps) {
      if (DESTRUCTION.has(event.type) && !running.has(event.robotId)) {
        const from = settled.current.get(event.robotId);
        if (from) running.set(event.robotId, from);
      }
    }
    setPositions(new Map(running));

    // The board as the animation has it. A missile moves under its own
    // power and a flag rides a belt, so both are walked here rather than
    // swapped in at the end of the register.
    let layers = layersBefore;

    steps.forEach((step, i) => {
      timers.current.push(setTimeout(() => {
        if (cancelled.current) return;

        // Set for EVERY step, so a beat that is not a shot clears the one
        // before it. Read from `running`, which is where the board has
        // the robots at this beat -- the view's positions are the end of
        // the whole register, and would draw the line from where the
        // shooter finished rather than from where it fired.
        setShot(shotFrom(step, running));

        // Taken out of the way of the robot steps, as a missile's are:
        // these carry no robot id at all, so anything below that reads
        // `robotId` as the thing that moved would act on a robot that
        // does not exist.
        if (FLAG_STEPS.has(step.type)) {
          layers = { ...layers, flags: afterFlagStep(layers.flags, step) };
          setHeld(layers);
          if (i === steps.length - 1) endRun();
          return;
        }

        // An archive is taken where the robot is standing, which is where
        // the board has it at this beat rather than where the register
        // left it.
        if (ARCHIVE_STEPS.has(step.type)) {
          layers = {
            ...layers,
            archives: afterArchiveStep(layers.archives, step, running.get(step.robotId)),
          };
          setHeld(layers);
          if (i === steps.length - 1) endRun();
          return;
        }

        // Missiles first: their events carry the FIRER's id, so anything
        // below that reads `robotId` as the thing that moved would fly
        // the shooter across the board behind its own shell.
        if (MISSILE_STEPS.has(step.type)) {
          layers = { ...layers, tokens: afterMissileStep(layers.tokens, step) };
          setHeld(layers);
          if (i === steps.length - 1) endRun();
          return;
        }

        const previous = running.get(step.robotId);

        if (DESTRUCTION.has(step.type)) {
          if (previous) {
            setWreckage((current) => new Map(current).set(step.robotId, previous));
          }
        } else if (step.to !== undefined) {
          running.set(step.robotId, {
            x: step.to.x,
            y: step.to.y,
            // A move that did not change facing leaves it alone, so a
            // rotation earlier in the same register is not undone.
            facing: step.facing ?? previous?.facing ?? 'N',
            damage: previous?.damage ?? 0,
          });
        } else if (previous) {
          // A hit: the robot stays where it is and the token ticks up at
          // the moment the shot lands.
          running.set(step.robotId, {
            ...previous,
            damage: previous.damage + damageFrom(step),
          });
        }

        setPositions(new Map(running));
        if (i === steps.length - 1) endRun();
      }, STEP_MS * (i + 1)));
    });

    // No teardown. A run owns its timers until it ends or the phase
    // does, and the `inFlight` guard above is what keeps StrictMode's
    // second mount from starting a second copy of it.
  }, [version, enabled, entries, logLoaded, ended]);

  return {
    positions,
    playing,
    shot,
    wreckage,
    tokens: held?.tokens ?? null,
    flags: held?.flags ?? null,
    archives: held?.archives ?? null,
  };
}
